import dynamic from 'next/dynamic';

import { Separator } from '@natu/ui/Decorators/Separator';
import { Share } from '@natu/ui/Share';
import { isArrayWithLength } from '@natu/utils/isArrayWithLength';

import {
  BlokItem,
  getAssetFromStoryblok,
  sbEditable,
  StoryblokAsset,
  type SBProps,
} from '../../../../../utils';
import {
  ArticleNodeAuthor,
  ArticleNodeCategory,
} from '../../../../../utils/src/types/Blog/ArticleNode.types';
import { ArticleAuthor } from './components/ArticleAuthor';
import { ArticleHeader } from './components/ArticleHeader';
import { ArticleSchema } from './components/ArticleSchema';

const DynamicRender = dynamic(() => import('../../../../../utils').then(mod => mod.DynamicRender));

const ResponsiveImage = dynamic(() =>
  import('@natu/ui/ResponsiveImage').then(mod => mod.ResponsiveImage),
);

const ArticleCategoryTag = dynamic(() =>
  import('./components/ArticleCategoryTag').then(mod => mod.ArticleCategoryTag),
);

interface SBArticleProps {
  title?: string;
  description?: string;
  author?: ArticleNodeAuthor;
  timeToRead?: string;
  previewImage?: StoryblokAsset;
  beforeBody?: BlokItem[];
  body?: BlokItem[];
  afterBody?: BlokItem[];
  categories?: ArticleNodeCategory[];
  parentProps?: {
    slug?: string;
    firstPublishedAt?: string;
  };
  advancedSeo?: BlokItem[];
}

export const SBArticle = ({ blok }: SBProps<SBArticleProps>) => {
  const {
    title,
    description,
    author,
    timeToRead,
    previewImage,
    beforeBody,
    body,
    afterBody,
    categories,
    parentProps,
    advancedSeo,
  } = blok;

  const image = getAssetFromStoryblok(previewImage, { type: 'image' });

  const articleParentProps = {
    ...parentProps,
    title,
    description,
    author,
    previewImage: previewImage?.filename,
    authorImage: author?.content?.image?.filename,
  };

  const schemaProps = {
    firstPublishedAt: parentProps?.firstPublishedAt,
    slug: parentProps?.slug,
    title,
    description,
    author,
    previewImage: previewImage?.filename,
    authorImage: author?.content?.image?.filename,
  };

  return (
    <>
      {advancedSeo && <DynamicRender data={advancedSeo} parentProps={articleParentProps} />}
      <ArticleSchema {...schemaProps} />
      <main {...sbEditable(blok)}>
        <DynamicRender data={beforeBody} />
        <div className="mx-auto max-w-screen-xl px-4 md:px-6 lg:px-8">
          <div className="mx-auto mb-12 mt-10 max-w-[736px] lg:mb-16 lg:mt-[72px]">
            <ArticleHeader title={title} description={description} />
            <div className="mt-10 flex flex-col gap-6 md:flex-row md:justify-between">
              <ArticleAuthor
                author={author}
                date={parentProps?.firstPublishedAt}
                readTime={timeToRead}
              />
              <Share />
            </div>
          </div>
          {image?.src && (
            <ResponsiveImage className="my-12 rounded-lg lg:mb-24 lg:mt-16" priority {...image} />
          )}
        </div>
        <DynamicRender data={body} />
        <Separator className="mx-auto mb-6 mt-10 max-w-[736px] md:mt-12 lg:mb-8" />
        <div className="mx-auto flex w-full max-w-[800px] flex-col gap-6 px-4 md:flex-row md:items-center md:justify-between md:px-6 lg:px-8">
          {isArrayWithLength(categories) && (
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <ArticleCategoryTag key={category.id} category={category} />
              ))}
            </div>
          )}
          <Share />
        </div>
        <DynamicRender data={afterBody} />
      </main>
    </>
  );
};
